import { MdCheckCircleOutline } from 'react-icons/md';
import { Checkbox } from '@Components/FormCheck';
import { IconButton } from '@Components/IconButton';
import {
  CustomModalEditWidgetHeader,
  SelectorOption
} from '@Components/ModalEditWidget/modal-edit-constants';

export const PAGE_TITLE = 'Data Connector Tool';
export const PAGE_TYPE = 'Data Connector Tool';
export const ROW_HEIGHT = 50;

// The full set of valid `source` values.
export const DataConnectorSource = {
  FACEBOOK: 'facebook',
  GA4: 'ga4',
  BING: 'bing',
  GOOGLE_ADS: 'google_ads',
  SKAI: 'skai',
  TIKTOK: 'tiktok',
  TRADEDESK: 'tradedesk'
} as const;
export type DataConnectorSourceType =
  (typeof DataConnectorSource)[keyof typeof DataConnectorSource];

export interface DataConnectorRow {
  company: string;
  source: DataConnectorSourceType;
  account_id: string;
  secret_path: string;
  mcc_id: string;
  account_name?: string;
  currency_code?: string;
  enabled?: boolean;
  // Only set for rows the backend has actually returned -- a row staged in the Add modal has no
  // `created` yet.
  created?: string;
  lastmodified: string;
  lastuser: string;
}

/** Stable row key for ModalEditWidget's getRowId -- mirrors the backend's own uniqueness check
 * (company + source + account_id) in putMobiusDataConnectorJob. Takes just the three key fields
 * (rather than a full DataConnectorRow) so callers like the auth-check flow, which only ever have
 * those three fields on hand, can reuse it instead of re-deriving the same format by hand. */
export const getDataConnectorRowId = (
  row: Pick<DataConnectorRow, 'company' | 'source' | 'account_id'>
): string => `${row.company}_${row.source}_${row.account_id}`;

// The only ever-populated field: putMobiusDataConnectorJob only processes `insert`, and its
// `assertBodyWrapper([])` requires no fields at all, so `update`/`delete` (which this page never
// has anything to send for -- enableDelete={false}, and edits are never persisted) are omitted
// entirely rather than sent as literal empty arrays.
export interface DataConnectorRowParams {
  insert: DataConnectorRow[];
}

export const SOURCE_OPTIONS: SelectorOption[] = [
  { value: DataConnectorSource.FACEBOOK, label: 'Facebook' },
  { value: DataConnectorSource.GA4, label: 'Google Analytics 4' },
  { value: DataConnectorSource.BING, label: 'Bing Ads (Microsoft)' },
  { value: DataConnectorSource.GOOGLE_ADS, label: 'Google Ads' },
  { value: DataConnectorSource.SKAI, label: 'Skai' },
  { value: DataConnectorSource.TIKTOK, label: 'TikTok' },
  { value: DataConnectorSource.TRADEDESK, label: 'TradeDesk' }
];

export const SECRET_OPTIONS: SelectorOption[] = [
  { value: 'production/google_ads/data-1', label: 'Google Ads: data-1@' },
  { value: 'production/google_ads/data', label: 'Google Ads: data@' },
  { value: 'production/google_analytics/data-1', label: 'Google Analytics 4: data-1@' },
  { value: 'production/google_analytics/data', label: 'Google Analytics 4: data@' },
  { value: 'production/bing_ads/data', label: 'Bing Ads: data@' },
  { value: 'production/bing_ads/data-1', label: 'Bing Ads: data-1@' },
  { value: 'production/facebook_ads', label: 'Facebook: dataservicesplatforms@' },
  { value: 'production/facebook/data', label: 'Facebook: data@' },
  { value: 'production/skai/data', label: 'Skai: data@' },
  { value: 'prod/tiktok/data', label: 'TikTok: data@' },
  { value: 'prod/tradedesk/ttd_api_reporting', label: 'TradeDesk: ttd_api_m3qvhnc_reporting@' }
];

const MAIN_MODAL_WIDTH = 200;

const WIDTHS = {
  CHECK_AUTH: 60,
  COMPANY: 120,
  SOURCE: 96,
  ACCOUNT_ID: 160,
  SECRET_PATH: 220,
  ACCOUNT_NAME: 200,
  MCC_ID: 120,
  CURRENCY_CODE: 80,
  LAST_MODIFIED: 160,
  LAST_USER: 160,
  ENABLED: 80
} as const;

const MODAL_ROWS = {
  COMPANY: 0,
  SOURCE: 0,
  ACCOUNT_ID: 0,
  SECRET_PATH: 1,
  MCC_ID: 1
} as const;

export const buildDataConnectorHeaders = (
  checkAuthLoadingKey: string | null,
  onCheckAuth: (row: DataConnectorRow) => void,
  onToggleEnabled: (row: DataConnectorRow) => void
): CustomModalEditWidgetHeader[] => [
  {
    label: 'Check Auth',
    field: 'checkAuth',
    width: WIDTHS.CHECK_AUTH,
    uneditable: true,
    nonInteractive: true,
    renderer: (row: DataConnectorRow) => (
      <div className='cell-centered'>
        <IconButton
          data-testid={`check-auth-${getDataConnectorRowId(row)}`}
          onClick={() => onCheckAuth(row)}
          loading={checkAuthLoadingKey === getDataConnectorRowId(row)}
          disabled={checkAuthLoadingKey !== null}
        >
          <MdCheckCircleOutline />
        </IconButton>
      </div>
    )
  },
  {
    label: 'Company',
    field: 'company',
    type: 'select',
    options: 'company_options',
    searchable: true,
    width: WIDTHS.COMPANY,
    modalRow: MODAL_ROWS.COMPANY,
    modalWidth: MAIN_MODAL_WIDTH,
    modalFlex: 1
  },
  {
    label: 'Source',
    field: 'source',
    type: 'select',
    options: 'source_options',
    width: WIDTHS.SOURCE,
    modalRow: MODAL_ROWS.SOURCE,
    modalWidth: MAIN_MODAL_WIDTH
  },
  {
    label: 'Account Id',
    field: 'account_id',
    type: 'text',
    width: WIDTHS.ACCOUNT_ID,
    modalRow: MODAL_ROWS.ACCOUNT_ID,
    modalWidth: MAIN_MODAL_WIDTH
  },
  {
    label: 'Auth Account',
    field: 'secret_path',
    type: 'select',
    options: 'secret_options',
    searchable: true,
    width: WIDTHS.SECRET_PATH,
    modalRow: MODAL_ROWS.SECRET_PATH,
    modalWidth: MAIN_MODAL_WIDTH,
    modalFlex: 1
  },
  {
    label: 'Account Name',
    field: 'account_name',
    uneditable: true,
    width: WIDTHS.ACCOUNT_NAME,
    minFlexWidth: WIDTHS.ACCOUNT_NAME,
    flex: 1
  },
  {
    label: 'MCC Id',
    field: 'mcc_id',
    type: 'text',
    width: WIDTHS.MCC_ID,
    modalRow: MODAL_ROWS.MCC_ID,
    modalWidth: MAIN_MODAL_WIDTH
  },
  {
    label: 'Currency Code',
    field: 'currency_code',
    uneditable: true,
    width: WIDTHS.CURRENCY_CODE
  },
  {
    label: 'Last Modified',
    field: 'lastmodified',
    type: 'timestamp',
    uneditable: true,
    width: WIDTHS.LAST_MODIFIED
  },
  {
    label: 'Last User',
    field: 'lastuser',
    uneditable: true,
    width: WIDTHS.LAST_USER,
    // lastuser is a full email -- strip the domain.
    renderer: (row: DataConnectorRow) => row.lastuser?.split('@')[0]
  },
  {
    label: 'Enabled',
    field: 'enabled',
    uneditable: true,
    nonInteractive: true,
    width: WIDTHS.ENABLED,
    // The checkbox is both the status indicator and the toggle -- checking/unchecking it opens
    // the confirm dialog (onToggleEnabled) rather than flipping state directly. Disabled below for
    // a row still staged in the Add modal (no `created` yet), since the toggle endpoint keys on
    // source + account_id with no company -- without the guard, a staged row could flip an
    // unrelated existing row that happens to collide on those two fields. That collision risk is
    // still real between two already-*saved* rows across different companies, just not from here.
    renderer: (row: DataConnectorRow) => (
      <div className='cell-centered'>
        <Checkbox
          data-testid={`toggle-enabled-${getDataConnectorRowId(row)}`}
          checked={Boolean(row.enabled)}
          disabled={!row.created}
          onChange={() => onToggleEnabled(row)}
        />
      </div>
    )
  }
];
